// src/ReportsQueuePage.js
// The moderation queue: everything users have reported through content_reports,
// split by what was reported, with a status an admin can move through.
//
// Stored status -> label shown:
//   pending -> New, reviewed -> Under Review, escalated -> Escalated,
//   resolved -> Resolved, dismissed -> Dismissed.
//
// Safety concerns raised from a booking ("Safety or conduct concern") live in
// the Contact Us queue, not in content_reports; the Safety tab lists those
// read-only next to harassment reports so one tab shows everything safety.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, Select, MenuItem, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Alert,
} from '@mui/material';
import { supabase } from './supabaseClient';
import { ink, surface } from './designTokens';

import { prettyStatus } from './labels';
const STATUS_LABEL = {
  pending: 'New',
  reviewed: 'Under Review',
  escalated: 'Escalated',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};
const STATUS_COLOR = {
  pending: 'warning', reviewed: 'info', escalated: 'error', resolved: 'success', dismissed: 'default',
};

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'user', label: 'Users' },
  { key: 'message', label: 'Messages' },
  { key: 'image', label: 'Photos' },
  { key: 'profile', label: 'Profiles' },
  { key: 'review', label: 'Reviews' },
  { key: 'safety', label: 'Safety' },
];

const tabOf = (r) =>
  ['message', 'image', 'profile', 'review'].includes(r.content_type) ? r.content_type : 'user';

export default function ReportsQueuePage() {
  const [reports, setReports] = useState([]);
  const [safetyTickets, setSafetyTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('open');
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('content_reports')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500);
    if (err) setError(err.message);
    setReports(data || []);
    const { data: tickets } = await supabase
      .from('Contact Us')
      .select('*')
      .ilike('category', '%safety%')
      .order('created_at', { ascending: false })
      .limit(100);
    setSafetyTickets(tickets || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = reports.filter((r) => {
    const inTab = tab === 'all' || (tab === 'safety' ? r.report_type === 'harassment' : tabOf(r) === tab);
    const status = r.status || 'pending';
    const inStatus =
      statusFilter === 'all' ||
      (statusFilter === 'open' ? ['pending', 'reviewed', 'escalated'].includes(status) : status === statusFilter);
    return inTab && inStatus;
  });

  const counts = (key) =>
    reports.filter((r) => (r.status || 'pending') === 'pending' &&
      (key === 'all' || (key === 'safety' ? r.report_type === 'harassment' : tabOf(r) === key))).length;

  const setStatus = async (report, status) => {
    setSaving(true);
    const { data: auth } = await supabase.auth.getUser();
    const patch = {
      status,
      handled_by: auth?.user?.email || null,
      handled_at: new Date().toISOString(),
      resolution_note: note.trim() || report.resolution_note || null,
    };
    const { error: err } = await supabase.from('content_reports').update(patch).eq('id', report.id);
    if (err) {
      setError(`Could not update: ${err.message}`);
    } else {
      try {
        await supabase.rpc('write_admin_audit', {
          p_action: 'report_status_changed',
          p_entity_type: 'content_report',
          p_entity_id: report.id,
          p_payload: { from: report.status, to: status, note: patch.resolution_note },
        });
      } catch (_) { /* the row itself records who and when */ }
      setDetail((d) => (d ? { ...d, ...patch } : d));
      await load();
    }
    setSaving(false);
  };

  // A reported review can be hidden from here without leaving the queue.
  const hideReportedReview = async (report) => {
    const { error: err } = await supabase
      .from('reviews')
      .update({ hidden_at: new Date().toISOString(), hidden_reason: `Reported: ${report.report_type}` })
      .eq('id', report.content_id);
    if (err) { setError(`Could not hide the review: ${err.message}`); return; }
    await setStatus(report, 'resolved');
  };

  return (
    <Box sx={{ p: 4, bgcolor: surface.page, minHeight: '100vh' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Reports queue</Typography>
      <Typography variant="body2" sx={{ color: ink[500], mb: 2 }}>
        Reports from users about people, messages, photos, profiles and reviews. Apple expects action within 24 hours.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: `1px solid ${surface.line}`, borderRadius: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
            {TABS.map((t) => (
              <Tab key={t.key} value={t.key} label={`${t.label}${counts(t.key) ? ` (${counts(t.key)})` : ''}`} />
            ))}
          </Tabs>
          <Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="all">All statuses</MenuItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </Select>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper elevation={0} sx={{ border: `1px solid ${surface.line}`, borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: surface.sunken }}>
                <TableCell>Reported</TableCell>
                <TableCell>About</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Reporter</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', color: ink[500] }}>Nothing in this queue.</TableCell></TableRow>
              )}
              {visible.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{new Date(r.created_at).toLocaleString()}</TableCell>
                  <TableCell>{r.content_type || 'user'} · {r.reported_phone_number || '—'}</TableCell>
                  <TableCell>{r.report_type}{r.description ? ` — ${r.description}` : ''}</TableCell>
                  <TableCell>{r.reporter_phone_number}</TableCell>
                  <TableCell>
                    <Chip size="small" color={STATUS_COLOR[r.status || 'pending']} label={STATUS_LABEL[r.status || 'pending'] || prettyStatus(r.status)} />
                  </TableCell>
                  <TableCell><Button size="small" onClick={() => { setDetail(r); setNote(r.resolution_note || ''); }}>Open</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 'safety' && safetyTickets.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Safety concerns raised from bookings (handled in Contact Us)
          </Typography>
          <Paper elevation={0} sx={{ border: `1px solid ${surface.line}`, borderRadius: 3 }}>
            <Table size="small">
              <TableBody>
                {safetyTickets.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{t.created_at ? new Date(t.created_at).toLocaleString() : '—'}</TableCell>
                    <TableCell>{t.subject || t.category}</TableCell>
                    <TableCell><Chip size="small" label={prettyStatus(t.status) || 'Open'} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        {detail && (
          <>
            <DialogTitle>
              {detail.content_type || 'User'} report · {STATUS_LABEL[detail.status || 'pending']}
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2">Reason: {detail.report_type}</Typography>
              <Typography variant="body2">Reporter: {detail.reporter_phone_number}</Typography>
              <Typography variant="body2">About: {detail.reported_phone_number || '—'}</Typography>
              {detail.content_id && <Typography variant="body2">Content id: {detail.content_id}</Typography>}
              <Typography variant="body2" sx={{ my: 1 }}>{detail.description || 'No details given.'}</Typography>
              {detail.handled_by && (
                <Typography variant="caption" sx={{ color: ink[500] }}>
                  Last handled by {detail.handled_by} on {new Date(detail.handled_at).toLocaleString()}
                </Typography>
              )}
              <TextField
                fullWidth multiline minRows={2} size="small" sx={{ mt: 2 }}
                label="Resolution note" value={note} onChange={(e) => setNote(e.target.value)}
              />
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {detail.content_type === 'review' && detail.content_id && (
                <Button color="error" disabled={saving} onClick={() => hideReportedReview(detail)}>Hide review + resolve</Button>
              )}
              <Button disabled={saving} onClick={() => setStatus(detail, 'reviewed')}>Under review</Button>
              <Button disabled={saving} color="error" onClick={() => setStatus(detail, 'escalated')}>Escalate</Button>
              <Button disabled={saving} onClick={() => setStatus(detail, 'dismissed')}>Dismiss</Button>
              <Button disabled={saving} variant="contained" onClick={() => setStatus(detail, 'resolved')}>Resolve</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
